import React from 'react';
import useScrollReveal, { useStaggerReveal } from '../hooks/useScrollReveal';

const PromptAndParadox = () => {
  const [headerRef, headerVisible] = useScrollReveal();
  const [shortsRef, , getShortProps] = useStaggerReveal(3, { staggerDelay: 0.15 });
  const [deepRef, deepVisible] = useScrollReveal();
  const shorts = [
    { id: 'isQIGQOUSck', title: 'AI Short #1' },
    { id: 'IQ7r1wVEnt8', title: 'AI Short #2' },
    { id: 'DNTqgyiWUIM', title: 'AI Short #3' },
  ];

  return (
    <section id="content" className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-80 h-80 bg-gradient-to-br from-rose-200/30 to-pink-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-br from-violet-200/20 to-purple-200/20 rounded-full blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto relative">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-12 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-gradient-to-r from-rose-100 to-violet-100 text-rose-700 text-sm font-medium mb-4">
            Content Creation
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            <span className="bg-gradient-to-r from-rose-600 to-violet-600 bg-clip-text text-transparent">Prompt</span>And<span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">Paradox</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            My creative outlet exploring AI's impact on the world through short-form video content.
            Video editing is a side passion that lets me blend storytelling with technology.
            Recently, I've also started creating technical deep-dive videos breaking down core AI concepts.
          </p>
        </div>

        {/* Channel Links */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <a
            href="https://www.youtube.com/@PromptAndParadox"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-5 py-2.5 rounded-full font-medium bg-gradient-to-r from-red-500 to-red-600 text-white hover:shadow-lg transition-all duration-300 hover:scale-[1.02] text-sm"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            YouTube
          </a>
          <a
            href="https://www.instagram.com/promptandparadox"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-5 py-2.5 rounded-full font-medium bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white hover:shadow-lg transition-all duration-300 hover:scale-[1.02] text-sm"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
            </svg>
            Instagram
          </a>
        </div>

        {/* Shorts */}
        <div className="mb-14">
          <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
            AI <span className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 bg-clip-text text-transparent">Shorts</span>
          </h3>
          <div ref={shortsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {shorts.map((short, i) => {
              const shortItemProps = getShortProps(i);
              return (
              <div
                key={short.id}
                className={`rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg bg-white ${shortItemProps.className}`}
                style={shortItemProps.style}
              >
                <div className="relative w-full" style={{ paddingBottom: '177.78%' }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${short.id}`}
                    className="absolute inset-0 w-full h-full"
                    style={{ border: 'none' }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                    title={short.title}
                  />
                </div>
              </div>
            );
            })}
          </div>
        </div>

        {/* Technical Videos */}
        <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
          Technical <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Deep Dives</span>
        </h3>
        <div
          ref={deepRef}
          className={`max-w-3xl mx-auto rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg bg-white transition-all duration-700 ${deepVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src="https://www.youtube.com/embed/KyjOW6VruKM"
              className="absolute inset-0 w-full h-full"
              style={{ border: 'none' }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
              title="Embeddings - Technical Deep Dive"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default PromptAndParadox;
